import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ADAARIARoles } from '../../../../models/ADA';
import { getViewportDimensions } from '../../../../models/Utils';

import './MosaicColumn.scss';

const appearanceThreshold = 10;

const MosaicTextBox = ({ title, body, scroller, fadingAllowed }) => {
  const element = useRef(null);
  const [ isVisible, setIsVisible ] = useState(true);
  const isVisibleRef = useRef(isVisible);
  const menuOpened = useSelector(state => state.data.menuOpened);

  const handleScroll = () => {
    if (!fadingAllowed.current) return;
    const viewport = getViewportDimensions();
    const rect = element.current.getBoundingClientRect();
    if (isVisibleRef.current && rect.x + appearanceThreshold / 2 >= viewport.width) {
      setIsVisible(false);
      isVisibleRef.current = false;
    } else if (!isVisibleRef.current && rect.x + appearanceThreshold < viewport.width) {
      setIsVisible(true);
      isVisibleRef.current = true;
    };
  };

  useLayoutEffect(() => {
    setTimeout(() => {
      handleScroll();
      scroller.current.addEventListener('scroll', handleScroll);
    }, 100);

    return () => {
      if (scroller.current) scroller.current.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={`bbf-mc-text-box ${ isVisible ? 'show-text' : ''}`} 
      ref={element}>
      <div className="bbf-mc-text-box-text title">{title}</div>
      <div className="bbf-mc-text-box-text body">{body}</div>
    </div>
  );
};

const MosaicImage = ({ size, textPosition, image, children, scroller, fadingAllowed, altText }) => {
  const element = useRef(null);
  const [ isVisible, setIsVisible ] = useState(true);
  const isVisibleRef = useRef(isVisible);
  const menuOpened = useSelector(state => state.data.menuOpened);

  const handleScroll = () => {
    if (!fadingAllowed.current) return;
    const viewport = getViewportDimensions();
    const rect = element.current.getBoundingClientRect();
    if (isVisibleRef.current && rect.x + appearanceThreshold / 2 >= viewport.width) {
      setIsVisible(false);
      isVisibleRef.current = false;
    } else if (!isVisibleRef.current && rect.x + appearanceThreshold < viewport.width) {
      setIsVisible(true);
      isVisibleRef.current = true;
    };
  };

  useLayoutEffect(() => {
    setTimeout(() => {
      handleScroll();
      scroller.current.addEventListener('scroll', handleScroll);
    }, 100);

    return () => {
      if (scroller.current) scroller.current.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (size === 'big' && !textPosition) {
    throw new Error("Big mosaic image must have text position defined.");
  }

  return (
    <div className={`bbf-mc-image ${size ? size : ''} ${textPosition ? textPosition : ''}`}>
      <div
        className={`bbf-mc-image-inner ${isVisible ? 'show-image' : ''}`}
        style={{ backgroundImage: `url("${image}")` }}
        ref={element} role="img" alt={altText} aria-hidden={true}
      ></div>
      {children}
    </div>
  );
};

const MosaicRow = ({ textPosition, children }) => {
  if (!textPosition) {
    throw new Error('Row must have text position');
  }
  return (
    <div className={`bbf-mc-row ${textPosition}`}>
      {children}
    </div>
  );
};

function getHalfColumn(data, scroller, fadingAllowed) {
  return (
    <div className={`bbf-mc-half-column type-${data.Type}`}>
      {
        data.Type === '1'
        ?
        <React.Fragment>
          <MosaicTextBox
            scroller={scroller}
            fadingAllowed={fadingAllowed}
            title={data.Text1.Title}
            body={data.Text1.Body}
          />
          <MosaicImage
            scroller={scroller}
            fadingAllowed={fadingAllowed}
            size="small"
            image={data.Image1.Image}
            altText={data.Image1.AltText}
          />
          <MosaicTextBox
            scroller={scroller}
            fadingAllowed={fadingAllowed}
            title={data.Text2.Title}
            body={data.Text2.Body}
          />
        </React.Fragment>
        :
        <React.Fragment>
          <MosaicImage
            scroller={scroller}
            fadingAllowed={fadingAllowed}
            size="small"
            image={data.Image1.Image}
            altText={data.Image1.AltText}
          />
          <MosaicTextBox
            scroller={scroller}
            fadingAllowed={fadingAllowed}
            title={data.Text1.Title}
            body={data.Text1.Body}
          />
          <MosaicImage
            scroller={scroller}
            fadingAllowed={fadingAllowed}
            size="small"
            image={data.Image2.Image}
            altText={data.Image2.AltText}
          />
        </React.Fragment>
      }
    </div>
  );
}

function getFullColumn(data, scroller, fadingAllowed) {
  const type = data.Type === '1' ? 'row-first' : 'image-first';
  const rowTextPosition = data.RowTextPosition === '1' ? 'text-right' : 'text-left';
  let bigImageTextPosition;
  switch (data.BigImageTextPosition) {
    case '0':
      bigImageTextPosition = 'text-top-left';
      break;
    case '1':
      bigImageTextPosition = 'text-top-right';
      break;
    case '2':
      bigImageTextPosition = 'text-bottom-left';
      break;
    case '3':
      bigImageTextPosition = 'text-bottom-right';
      break;
    default:
      bigImageTextPosition = 'no-text';
  }

  return (
    <div className={`bbf-mc-full-column ${type}`}>
      <MosaicRow textPosition={rowTextPosition}>
        <MosaicTextBox
          scroller={scroller}
          fadingAllowed={fadingAllowed}
          title={data.RowText.Title}
          body={data.RowText.Body}
        />
        <MosaicImage
          scroller={scroller}
          fadingAllowed={fadingAllowed}
          size="small"
          image={data.RowImage.Image}
          altText={data.RowImage.AltText}
        />
      </MosaicRow>
      <MosaicImage
        scroller={scroller}
        fadingAllowed={fadingAllowed}
        size="big"
        textPosition={bigImageTextPosition}
        image={data.BigImage.Image}
        altText={data.BigImage.AltText}
      >
        <MosaicTextBox
          scroller={scroller}
          fadingAllowed={fadingAllowed}
          title={data.BigImageText.Title}
          body={data.BigImageText.Body}
        />
      </MosaicImage>
    </div>
  );
}

const MosaicColumn = React.forwardRef(({ unitWidth, columnType, data }, { scroller, wiggleEnded }) => {
  const isHalf = columnType === '1';
  const fadingAllowed = wiggleEnded;

  const getFontSize = (unitWidth) => {
    if (unitWidth < 115) {
      return 8;
    } else if (unitWidth < 130) {
      return 10;
    } else if (unitWidth < 140) {
      return 12;
    } else if (unitWidth < 160) {
      return 14;
    } else if (unitWidth < 180) {
      return 16;
    } else if (unitWidth < 200) {
      return 18;
    }
    else {
      return 20;
    }
  };

  return (
    <div className={`bbf-mosaic-column font-${getFontSize(unitWidth)}`} style={{ width: `${ isHalf ? unitWidth : 2 * unitWidth }px` }}>
      { isHalf ? getHalfColumn(data, scroller, fadingAllowed) : getFullColumn(data, scroller, fadingAllowed) }
    </div>
  );
});

export default MosaicColumn;
